<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <h2 class="title-subsection text-ink">{{ heading }}</h2>
            <p v-if="subtitle" class="body-small text-ink-muted">{{ subtitle }}</p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <template v-if="hasAnyItems">
            <template v-for="(group, idx) in groups" :key="group.key">
                <HorizontalDivider v-if="group.key === 'status' && idx > 0" as="hr" spacing="none" class="mt-5" />
                <div v-if="group.items.length" class="flex flex-col gap-2">
                    <div class="scaffold-details-small text-ink-muted">{{ group.label }}</div>
                    <UpcomingItemRow v-for="item in group.items" :key="item.id" :item="item" @activate="onActivate" />
                </div>
            </template>
        </template>
        <template v-else>
            <div v-if="awaitingSelf.length" class="flex flex-col gap-2">
                <div class="scaffold-details-small text-ink-muted">{{ t("reflections.upcoming.awaitingSelfReflection") }}</div>
                <PastCycleCard v-for="cycle in awaitingSelf" :key="cycle.id" :cycle="cycle" kind="sixty" @view="emit('view-cycle', $event)" />
            </div>
            <div v-if="recentResults.length" class="flex flex-col gap-2" :class="{ 'mt-5': awaitingSelf.length }">
                <div class="scaffold-details-small text-ink-muted">{{ t("reflections.upcoming.recentResults") }}</div>
                <PastCycleCard
                    v-for="cycle in recentResults"
                    :key="cycle.id"
                    :cycle="cycle"
                    :kind="cycle.is_self_assessment_only ? 'self' : 'sixty'"
                    @view="emit('view-cycle', $event)" />
            </div>
            <div v-if="hasLaunchableCards" class="flex flex-col gap-2" :class="{ 'mt-5': hasHistory }">
                <div v-if="hasHistory" class="scaffold-details-small text-ink-muted">{{ t("reflections.upcoming.startSomethingNew") }}</div>
                <GettingStartedCard v-if="showSixtyCard" kind="sixty" :title="t('reflections.upcoming.card.sixtyTitle')" href="/reflections/360/start" @launch="emit('new-360')">
                    {{ t("reflections.upcoming.card.sixtyBody") }}
                </GettingStartedCard>
                <GettingStartedCard
                    v-if="showSelfCard"
                    kind="self"
                    :title="t('reflections.upcoming.card.selfTitle')"
                    href="/reflections/360/start?mode=self"
                    @launch="emit('start-blank')">
                    {{ t("reflections.self.howItWorks.body") }}
                </GettingStartedCard>
                <GettingStartedCard v-if="isTabAvailable('team', tabs)" kind="team" :title="t('reflections.upcoming.card.teamTitle')">
                    {{ t("reflections.upcoming.card.teamBody") }}
                </GettingStartedCard>
            </div>
        </template>
    </section>
</template>

<script lang="ts">
import type { ReflectionsTabKey } from "~vue/pages/reflections/ReflectionsSideNav.vue";

export type UpcomingItemKind = "team_reflection" | "self_reflection" | "self_reflection_campaign" | "cycle_setup" | "cycle_setup_campaign" | "active_360_status";

/** Return the hub section an upcoming row belongs to, so org tab settings gate it. */
export function tabForItemKind(kind: UpcomingItemKind): ReflectionsTabKey {
    switch (kind) {
        case "team_reflection":
            return "team";
        case "self_reflection":
        case "self_reflection_campaign":
            return "self";
        default:
            return "sixty";
    }
}

export interface UpcomingItem {
    id: string;
    kind: UpcomingItemKind;
    title: string;
    subtitle: string;
    chipLabel: string;
    ctaLabel: string;
    href: string;
    closesAt: string | null;
    // Present for the active-360-status row: shows progress bar + N of M copy
    // instead of a CTA that navigates away.
    progress?: { responded: number; total: number };
    // For rows that resolve to an existing cycle, the cycle id + display
    // title so the hub can mount the take flow / Nominate panel inline.
    cycleId?: string;
    // For campaign rows (self_reflection_campaign / cycle_setup_campaign),
    // the campaign id. Distinct from cycleId — the hub POSTs it to
    // /v1/reflections/cycles/from-campaign to materialize the actual cycle
    // before routing (Codex adversarial review flagged that reusing cycleId
    // for campaigns 404s /cycles/{id}).
    campaignId?: string;
    selfReflectionTitle?: string;
    // For team_reflection rows — lets the hub open the inline feedback panel
    // instead of navigating to the rater landing URL.
    inviteToken?: string;
    subjectFirstName?: string;
}
</script>

<script setup lang="ts">
import type { PastCycleSummary, ReflectionTabsConfig } from "~vue/api/generated/reflections";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import GettingStartedCard from "~vue/pages/reflections/GettingStartedCard.vue";
import PastCycleCard from "~vue/pages/reflections/PastCycleCard.vue";
import { isTabAvailable } from "~vue/pages/reflections/ReflectionsSideNav.vue";
import UpcomingItemRow from "~vue/pages/reflections/UpcomingItemRow.vue";
import { closesLabel } from "~vue/utils/threeSixty";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const RECENT_RESULTS_LIMIT = 3;

const {
    items,
    tabs,
    pastCycles = [],
    canStartSelf = false,
    canStart360 = false,
} = defineProps<{
    items: UpcomingItem[];
    tabs?: ReflectionTabsConfig;
    pastCycles?: PastCycleSummary[];
    // False while an active org campaign owns every template for that mode:
    // launching would create a cycle that doesn't count toward the campaign.
    canStartSelf?: boolean;
    canStart360?: boolean;
}>();
const { t } = useI18n();
const emit = defineEmits<{
    (event: "start-self", payload: { cycleId: string; title: string; closesAt: string | null }): void;
    (event: "view-cycle", cycleId: string): void;
    (event: "nominate", cycleId: string): void;
    // Fired for org-campaign rows. The hub POSTs to /cycles/from-campaign
    // first, then routes to take-self or nominate based on the returned cycle.
    (event: "start-from-campaign", payload: { campaignId: string; title: string; isSelfAssessmentOnly: boolean }): void;
    // Fired for the active-360 status row so the hub switches to the 360s
    // tab (where the rater list + Wrap Up live) instead of trying to open
    // the results panel for a still-collecting cycle.
    (event: "open-tab", tab: "sixty"): void;
    // Fired for team_reflection rows so the hub opens the inline feedback
    // panel — same behavior as the Team Reflections tab's Give feedback CTA.
    (event: "give-feedback", payload: { inviteToken: string; subjectFirstName: string }): void;
    // Getting-started rows, shown only when the tab has nothing to do.
    (event: "new-360"): void;
    (event: "start-blank"): void;
}>();

// Rows for a section the organization hides are dropped: that section's tab is
// already unreachable, so the row would be the only way into a hidden flow.
const visibleItems = computed(() => items.filter((item) => isTabAvailable(tabForItemKind(item.kind), tabs)));

// Group actionable items by their "Closes X" label so items sharing a deadline
// cluster under one caption; the active-360-status row lives in a separate
// "Status" group so it doesn't compete with the CTAs above it.
const groups = computed(() => {
    const actionable = visibleItems.value.filter((i) => i.kind !== "active_360_status");
    const status = visibleItems.value.filter((i) => i.kind === "active_360_status");
    const sorted = [...actionable].sort((a, b) => {
        const at = a.closesAt ? new Date(a.closesAt).getTime() : Number.POSITIVE_INFINITY;
        const bt = b.closesAt ? new Date(b.closesAt).getTime() : Number.POSITIVE_INFINITY;
        return at - bt;
    });
    const clusters: { key: string; label: string; items: UpcomingItem[] }[] = [];
    for (const item of sorted) {
        const label = closesLabel(item.closesAt) ?? t("reflections.upcoming.noDeadline");
        const last = clusters[clusters.length - 1];
        if (last && last.label === label) {
            last.items.push(item);
        } else {
            clusters.push({ key: `${label}-${item.id}`, label, items: [item] });
        }
    }
    if (status.length) {
        clusters.push({ key: "status", label: t("reflections.upcoming.statusGroup"), items: status });
    }
    return clusters;
});

const hasAnyItems = computed(() => visibleItems.value.length > 0);

const showSixtyCard = computed(() => canStart360 && isTabAvailable("sixty", tabs));
const showSelfCard = computed(() => canStartSelf && isTabAvailable("self", tabs));
// Team review has nothing to launch, so it only rides along with a card that does.
const hasLaunchableCards = computed(() => showSixtyCard.value || showSelfCard.value);

const visiblePastCycles = computed(() =>
    pastCycles.filter((cycle) => isTabAvailable(cycle.is_self_assessment_only ? "self" : "sixty", tabs)).toSorted((a, b) => completedAt(b) - completedAt(a)),
);
// A 360 held for the user's own reflection is unfinished work, not a result,
// and it has no Upcoming row of its own.
const awaitingSelf = computed(() => visiblePastCycles.value.filter((cycle) => cycle.awaiting_self_reflection));
// Reports that exist or are still generating; one closed without a report
// would only push a viewable result out of the list, and a draft that expired
// before launch is not a result at all.
const recentResults = computed(() =>
    visiblePastCycles.value
        .filter((cycle) => !cycle.never_started)
        .filter((cycle) => !cycle.awaiting_self_reflection && (cycle.has_report || (cycle.unlocked_at && !cycle.closed_without_report)))
        .slice(0, RECENT_RESULTS_LIMIT),
);
const hasHistory = computed(() => awaitingSelf.value.length > 0 || recentResults.value.length > 0);

function completedAt(cycle: PastCycleSummary): number {
    const time = Date.parse(cycle.unlocked_at ?? cycle.closed_at ?? "");
    return Number.isFinite(time) ? time : 0;
}

const heading = computed(() => {
    if (hasAnyItems.value || hasHistory.value) {
        return t("reflections.upcoming.title");
    }
    return hasLaunchableCards.value ? t("reflections.upcoming.gettingStarted") : t("reflections.upcoming.title");
});

const subtitle = computed(() => {
    if (!hasAnyItems.value) {
        if (awaitingSelf.value.length) {
            return "";
        }
        if (recentResults.value.length) {
            return t("reflections.upcoming.caughtUp");
        }
        return t("reflections.upcoming.subtitleEmpty");
    }
    // No "N things need you this week" tally: the rows below already list what
    // is outstanding, and the count made the header text-heavy.
    const collectingCount = visibleItems.value.filter((i) => i.kind === "active_360_status").length;
    if (!collectingCount) {
        return "";
    }
    return t("reflections.upcoming.roundsComing", { count: collectingCount }, collectingCount);
});

// Bubble intent up to the hub. Self-reflection cycles open the take flow
// inline; the active-360 status "View" opens the results panel; everything
// else falls back to href navigation the item row wires up as an <a>.
function onActivate(item: UpcomingItem) {
    if (item.kind === "team_reflection" && item.inviteToken) {
        emit("give-feedback", { inviteToken: item.inviteToken, subjectFirstName: item.subjectFirstName ?? "" });
        return;
    }
    if (item.kind === "self_reflection" && item.cycleId) {
        emit("start-self", { cycleId: item.cycleId, title: item.selfReflectionTitle ?? t("reflections.self.title"), closesAt: item.closesAt ?? null });
        return;
    }
    if (item.kind === "self_reflection_campaign" && item.campaignId) {
        // Campaigns don't have a cycle yet — the hub materializes one via
        // /cycles/from-campaign before routing.
        emit("start-from-campaign", { campaignId: item.campaignId, title: item.title, isSelfAssessmentOnly: true });
        return;
    }
    if (item.kind === "cycle_setup_campaign" && item.campaignId) {
        emit("start-from-campaign", { campaignId: item.campaignId, title: item.title, isSelfAssessmentOnly: false });
        return;
    }
    if (item.kind === "cycle_setup" && item.cycleId) {
        emit("nominate", item.cycleId);
        return;
    }
    if (item.kind === "active_360_status") {
        // Active cycles aren't unlocked yet, so opening ResultsPanel would 400
        // and the user would see a flash of nothing (Codex adversarial review).
        // Route to the 360s tab where the rater list + Wrap Up live.
        emit("open-tab", "sixty");
    }
}
</script>
